import { PerformanceAssessmentDocumentationMode } from '@/api/generated/api.enum';
import { getAssessmentDocumentationMode } from 'common/utils/performance.assessment.utils';
import { hasOverallAssessmentRating } from 'common/utils/performance.assessment.entry.utils';
import type { Components } from '@/api/generated/api';
import type { ISelectedCompetence } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import { isNotNullish, toRecord, uniquePrimitives } from 'common/utils/array.utils';
import { now } from 'common/utils/date.utils';
import { computed, reactive, ref, type ComputedRef, type Ref } from 'vue';
import { getEligibleStudentsForSubject, resolveStudentSelection } from '@/utils/students.utils';
import { EQuickEntryKind } from './quickEntry.enums';
import {
  buildQuickEntryCompetenceRelationMaps,
  computeSubjectChangeImpact,
  createEmptyQuickEntryDraft,
  getEligiblePerformanceAssessments,
  isQuickEntryFixedContextValid,
  serializeQuickEntryDraft,
  type IQuickEntryDraft,
  type IQuickEntryFixedContext,
  type IQuickEntryPreselection,
  type ISubjectChangeImpact,
} from './quickEntry.utils';
import { getQuickEntryValidationIssues, type TQuickEntryValidationIssue } from './quickEntry.validation';

export interface IQuickEntryDraftContext {
  allSchoolSubjects: Ref<Components.Schemas.SchoolSubjectDto[]>;
  allPerformanceAssessments: Ref<Components.Schemas.PerformanceAssessmentDto[]>;
  allStudents: Ref<Components.Schemas.StudentWithSubjectAccessDto[]>;
  allCompetenceDomains: Ref<Components.Schemas.CompetenceDomainDto[]>;
  defaultStudentIds: Ref<number[]>;
  fixedContext?: Ref<IQuickEntryFixedContext | undefined>;
}

export interface IQuickEntryDraftComposable {
  draft: IQuickEntryDraft;
  selectedSchoolSubject: ComputedRef<Components.Schemas.SchoolSubjectDto | null>;
  selectedPerformanceAssessment: ComputedRef<Components.Schemas.PerformanceAssessmentDto | null>;
  eligiblePerformanceAssessments: ComputedRef<Components.Schemas.PerformanceAssessmentDto[]>;
  eligibleStudents: ComputedRef<Components.Schemas.StudentWithSubjectAccessDto[]>;
  selectedStudents: ComputedRef<Components.Schemas.StudentWithSubjectAccessDto[]>;
  resolvedStudents: ComputedRef<Components.Schemas.StudentWithSubjectAccessDto[]>;
  resolvedStudentIds: ComputedRef<number[]>;
  competenceDomainsForSubject: ComputedRef<Components.Schemas.CompetenceDomainDto[]>;
  selectedCompetences: ComputedRef<ISelectedCompetence[]>;
  showOverallRating: ComputedRef<boolean>;
  isPristine: ComputedRef<boolean>;
  nextValidationIssue: ComputedRef<TQuickEntryValidationIssue | null>;
  canSubmit: ComputedRef<boolean>;
  fixedContextValid: ComputedRef<boolean>;
  setText: (text: string) => void;
  setDate: (date: string) => void;
  setOverallLevel: (value: number | null) => void;
  setCompetenceLevel: (competenceId: number, performanceLevelV2Id: number | null) => void;
  setGradeCalculationTagId: (gradeCalculationTagId: number | null) => void;
  addCompetence: (competenceId: number) => void;
  removeCompetence: (competenceId: number) => void;
  clearCompetences: () => void;
  addStudent: (studentId: number) => void;
  setStudents: (studentIds: number[]) => void;
  selectAllEligibleStudents: () => void;
  selectSubject: (schoolSubjectId: number | null) => void;
  selectPerformanceAssessment: (performanceAssessmentId: number) => void;
  selectNote: () => void;
  selectSupportMeasure: () => void;
  initFromPreselection: (preselection?: IQuickEntryPreselection) => void;
  initFromFilter: (schoolSubjectId: number | null, performanceAssessmentId?: number | null) => void;
  resetFromPreselection: (preselection?: IQuickEntryPreselection) => void;
  resetAfterSave: (filterSchoolSubjectId: number | null, filterPerformanceAssessmentId?: number | null) => void;
}

export const useQuickEntryDraft = (context: IQuickEntryDraftContext): IQuickEntryDraftComposable => {
  const draft = reactive(createEmptyQuickEntryDraft(now().toISOString()));
  const baseline = ref(serializeQuickEntryDraft(draft));

  const competenceRelationMaps = computed(() =>
    buildQuickEntryCompetenceRelationMaps(context.allCompetenceDomains.value),
  );
  const studentById = computed(() =>
    toRecord(
      context.allStudents.value,
      (student) => student.id,
      (student) => student,
    ),
  );

  const selectedSchoolSubject = computed(
    () => context.allSchoolSubjects.value.find((subject) => subject.id === draft.schoolSubjectId) ?? null,
  );

  const eligiblePerformanceAssessments = computed(() =>
    getEligiblePerformanceAssessments(context.allPerformanceAssessments.value, selectedSchoolSubject.value),
  );

  const selectedPerformanceAssessment = computed(
    () =>
      eligiblePerformanceAssessments.value.find((assessment) => assessment.id === draft.performanceAssessmentId) ??
      null,
  );

  const eligibleStudents = computed(() =>
    getEligibleStudentsForSubject(context.allStudents.value, draft.schoolSubjectId),
  );
  const eligibleStudentIds = computed(() => new Set(eligibleStudents.value.map((student) => student.id)));

  const explicitSelectedStudents = computed(() =>
    draft.studentIds.map((studentId) => studentById.value[studentId]).filter(isNotNullish),
  );
  const selectedStudents = computed(() => (draft.allEligibleStudentsSelected ? [] : explicitSelectedStudents.value));
  const resolvedStudents = computed(() =>
    resolveStudentSelection({
      allEligibleStudentsSelected: draft.allEligibleStudentsSelected,
      eligibleStudents: eligibleStudents.value,
      selectedStudents: explicitSelectedStudents.value,
    }),
  );
  const resolvedStudentIds = computed(() => resolvedStudents.value.map((student) => student.id));

  const competenceDomainsForSubject = computed(() => {
    const fixedDomainId = context.fixedContext?.value?.competenceDomainId;
    return context.allCompetenceDomains.value.filter(
      (domain) =>
        domain.schoolSubjectId === draft.schoolSubjectId &&
        (fixedDomainId === undefined || domain.id === fixedDomainId),
    );
  });

  const competenceSelectionById = computed(() => toCompetenceSelectionRecord(context.allCompetenceDomains.value));

  const selectedCompetences = computed(() =>
    draft.competenceIds.map((competenceId) => competenceSelectionById.value[competenceId]).filter(isNotNullish),
  );

  const showOverallRating = computed(() => hasOverallAssessmentRating(selectedPerformanceAssessment.value));

  const fixedContextValid = computed(() =>
    isQuickEntryFixedContextValid({
      draft,
      eligibleStudentIds: eligibleStudentIds.value,
      fixedContext: context.fixedContext?.value,
      relationMaps: competenceRelationMaps.value,
      selectedPerformanceAssessmentId: selectedPerformanceAssessment.value?.id ?? null,
      selectedSchoolSubjectId: selectedSchoolSubject.value?.id ?? null,
    }),
  );

  const isPristine = computed(() => serializeQuickEntryDraft(draft) === baseline.value);

  const validationIssues = computed(() =>
    getQuickEntryValidationIssues({
      draft,
      hasResolvedPerformanceAssessment: selectedPerformanceAssessment.value !== null,
      hasResolvedSchoolSubject: selectedSchoolSubject.value !== null,
      resolvedStudentCount: resolvedStudentIds.value.length,
    }),
  );

  const nextValidationIssue = computed<TQuickEntryValidationIssue | null>(() => {
    if (!fixedContextValid.value) return null;
    return validationIssues.value[0] ?? null;
  });

  const canSubmit = computed(() => fixedContextValid.value && validationIssues.value.length === 0);

  const markBaseline = (): void => {
    baseline.value = serializeQuickEntryDraft(draft);
  };

  const clearOverallRating = (): void => {
    draft.overallPerformanceLevelV2Id = null;
    draft.gradeValue = null;
  };

  const seedPerformanceAssessment = (): void => {
    if (draft.kind !== EQuickEntryKind.PerformanceAssessment) return;
    const fixedPerformanceAssessmentId = context.fixedContext?.value?.performanceAssessmentId;
    if (fixedPerformanceAssessmentId !== undefined) {
      draft.performanceAssessmentId = eligiblePerformanceAssessments.value.some(
        (assessment) => assessment.id === fixedPerformanceAssessmentId,
      )
        ? fixedPerformanceAssessmentId
        : null;
      return;
    }
    if (eligiblePerformanceAssessments.value.some((assessment) => assessment.id === draft.performanceAssessmentId)) {
      return;
    }
    // without a subject the shared assessments are offered but none is preselected,
    // so a later subject change never has to discard an assessment the user did not pick
    draft.performanceAssessmentId =
      draft.schoolSubjectId != null ? (eligiblePerformanceAssessments.value[0]?.id ?? null) : null;
    clearOverallRating();
  };

  const setText = (text: string): void => {
    draft.text = text;
  };

  const setDate = (date: string): void => {
    draft.date = date;
  };

  const setOverallLevel = (value: number | null): void => {
    const isGrade =
      getAssessmentDocumentationMode(selectedPerformanceAssessment.value) ===
      PerformanceAssessmentDocumentationMode.SCHOOL_GRADE;
    draft.gradeValue = isGrade ? value : null;
    draft.overallPerformanceLevelV2Id = isGrade ? null : value;
  };

  const setCompetenceLevel = (competenceId: number, performanceLevelV2Id: number | null): void => {
    if (!draft.competenceIds.includes(competenceId)) return;
    draft.competenceLevelById[competenceId] = performanceLevelV2Id;
  };

  const setGradeCalculationTagId = (gradeCalculationTagId: number | null): void => {
    draft.gradeCalculationTagId = gradeCalculationTagId;
  };

  const selectSubject = (schoolSubjectId: number | null): void => {
    const fixedSchoolSubjectId = context.fixedContext?.value?.schoolSubjectId;
    if (fixedSchoolSubjectId !== undefined && schoolSubjectId !== fixedSchoolSubjectId) return;
    if (schoolSubjectId === draft.schoolSubjectId) return;

    const newSchoolSubject = context.allSchoolSubjects.value.find((subject) => subject.id === schoolSubjectId) ?? null;
    const impact = computeSubjectChangeImpact({
      draft,
      newSchoolSubject,
      allCompetenceDomains: context.allCompetenceDomains.value,
      allStudents: context.allStudents.value,
      allPerformanceAssessments: context.allPerformanceAssessments.value,
    });

    draft.schoolSubjectId = newSchoolSubject?.id ?? null;
    applySubjectChangeImpact(impact);
    seedPerformanceAssessment();
  };

  const selectPerformanceAssessment = (performanceAssessmentId: number): void => {
    const fixedPerformanceAssessmentId = context.fixedContext?.value?.performanceAssessmentId;
    if (fixedPerformanceAssessmentId !== undefined && performanceAssessmentId !== fixedPerformanceAssessmentId) {
      return;
    }
    const next = eligiblePerformanceAssessments.value.find((assessment) => assessment.id === performanceAssessmentId);
    if (!next) return;
    draft.kind = EQuickEntryKind.PerformanceAssessment;
    if (next.id === draft.performanceAssessmentId) return;

    const previousDocumentationMode = getAssessmentDocumentationMode(selectedPerformanceAssessment.value);
    const nextDocumentationMode = getAssessmentDocumentationMode(next);
    const previousVisualId = selectedPerformanceAssessment.value?.performanceVisualId ?? null;
    draft.performanceAssessmentId = next.id;
    if (
      nextDocumentationMode === PerformanceAssessmentDocumentationMode.COMPETENCE_ONLY ||
      nextDocumentationMode !== previousDocumentationMode ||
      next.performanceVisualId !== previousVisualId
    ) {
      clearOverallRating();
    }
  };

  const selectNote = (): void => {
    if (context.fixedContext?.value?.performanceAssessmentId !== undefined) return;
    draft.kind = EQuickEntryKind.Note;
    draft.performanceAssessmentId = null;
    clearOverallRating();
    draft.gradeCalculationTagId = null;
    // notes never carry ratings; clearing here also keeps stale ratings from resurrecting
    // when the user switches back to a performance assessment later
    draft.competenceLevelById = Object.fromEntries(draft.competenceIds.map((competenceId) => [competenceId, null]));
  };

  const selectSupportMeasure = (): void => {
    if (context.fixedContext?.value?.performanceAssessmentId !== undefined) return;
    draft.kind = EQuickEntryKind.SupportMeasure;
    draft.performanceAssessmentId = null;
    clearOverallRating();
    draft.gradeCalculationTagId = null;
    draft.competenceLevelById = Object.fromEntries(draft.competenceIds.map((competenceId) => [competenceId, null]));
  };

  const addCompetence = (competenceId: number): void => {
    if (context.fixedContext?.value?.competenceIds !== undefined) return;
    const fixedDomainId = context.fixedContext?.value?.competenceDomainId;
    if (
      fixedDomainId !== undefined &&
      competenceRelationMaps.value.domainIdByCompetenceId.get(competenceId) !== fixedDomainId
    ) {
      return;
    }
    const competenceSubjectId = competenceRelationMaps.value.schoolSubjectIdByCompetenceId.get(competenceId);
    if (competenceSubjectId == null) return;

    if (draft.schoolSubjectId == null) selectSubject(competenceSubjectId);
    if (competenceSubjectId !== draft.schoolSubjectId) return;

    if (!draft.competenceIds.includes(competenceId)) {
      draft.competenceIds = [...draft.competenceIds, competenceId];
      draft.competenceLevelById[competenceId] = null;
    }
  };

  const removeCompetence = (competenceId: number): void => {
    if (context.fixedContext?.value?.competenceIds !== undefined) return;
    draft.competenceIds = draft.competenceIds.filter((id) => id !== competenceId);
    delete draft.competenceLevelById[competenceId];
  };

  const clearCompetences = (): void => {
    if (context.fixedContext?.value?.competenceIds !== undefined) return;
    draft.competenceIds = [];
    draft.competenceLevelById = {};
  };

  const collapseToAllEligibleStudentsWhenSelected = (): void => {
    if (draft.allEligibleStudentsSelected || eligibleStudents.value.length === 0) {
      return;
    }
    const selectedIds = new Set(draft.studentIds);
    if (eligibleStudents.value.every((student) => selectedIds.has(student.id))) selectAllEligibleStudents();
  };

  const normalizeEligibleStudentIds = (studentIds: number[]): number[] =>
    uniquePrimitives(studentIds).filter((studentId) => eligibleStudentIds.value.has(studentId));

  const addStudent = (studentId: number): void => {
    if (context.fixedContext?.value?.studentIds !== undefined) return;
    if (!eligibleStudentIds.value.has(studentId)) return;

    if (draft.allEligibleStudentsSelected) {
      draft.allEligibleStudentsSelected = false;
      draft.studentIds = [studentId];
    } else if (!draft.studentIds.includes(studentId)) {
      draft.studentIds = [...draft.studentIds, studentId];
    }
    collapseToAllEligibleStudentsWhenSelected();
  };

  const setStudents = (studentIds: number[]): void => {
    if (context.fixedContext?.value?.studentIds !== undefined) return;
    const normalizedStudentIds = normalizeEligibleStudentIds(studentIds);
    const selectsAllEligibleStudents = normalizedStudentIds.length === 0;
    // Deselecting the last student falls back to every student eligible for the active subject.
    draft.allEligibleStudentsSelected = selectsAllEligibleStudents;
    draft.studentIds = normalizedStudentIds;
    collapseToAllEligibleStudentsWhenSelected();
  };

  const selectAllEligibleStudents = (): void => {
    if (context.fixedContext?.value?.studentIds !== undefined) return;
    draft.allEligibleStudentsSelected = true;
    draft.studentIds = [];
  };

  const initFromPreselection = (preselection: IQuickEntryPreselection = {}): void => {
    const requestedSubjectId = context.fixedContext?.value?.schoolSubjectId ?? preselection.schoolSubjectId ?? null;
    const validSubject = context.allSchoolSubjects.value.find((subject) => subject.id === requestedSubjectId) ?? null;
    draft.schoolSubjectId = validSubject?.id ?? null;

    const requestedPerformanceAssessmentId =
      context.fixedContext?.value?.performanceAssessmentId ?? preselection.performanceAssessmentId ?? null;
    const filteredAssessment = findPreselectableAssessment(requestedPerformanceAssessmentId);
    if (filteredAssessment) {
      if (draft.schoolSubjectId == null && filteredAssessment.schoolSubjectId != null) {
        draft.schoolSubjectId = filteredAssessment.schoolSubjectId;
      }
      draft.kind = EQuickEntryKind.PerformanceAssessment;
      draft.performanceAssessmentId = filteredAssessment.id;
      clearOverallRating();
    } else if (
      context.fixedContext?.value?.performanceAssessmentId === undefined &&
      preselection.kind === EQuickEntryKind.SupportMeasure
    ) {
      draft.kind = EQuickEntryKind.SupportMeasure;
      draft.performanceAssessmentId = null;
      clearOverallRating();
    } else {
      const fixedPerformanceAssessmentId = context.fixedContext?.value?.performanceAssessmentId;
      if (fixedPerformanceAssessmentId === undefined) {
        selectNote();
      } else {
        draft.kind = EQuickEntryKind.PerformanceAssessment;
        draft.performanceAssessmentId = null;
        clearOverallRating();
      }
    }

    applyInitialStudents(preselection.studentIds);
    applyPreselectedCompetences(preselection.competenceIds);
    applyFixedCompetences();
    markBaseline();
  };

  const initFromFilter = (schoolSubjectId: number | null, performanceAssessmentId: number | null = null): void => {
    initFromPreselection({ schoolSubjectId, performanceAssessmentId });
  };

  const resetFromPreselection = (preselection: IQuickEntryPreselection = {}): void => {
    Object.assign(draft, createEmptyQuickEntryDraft(now().toISOString()));
    initFromPreselection(preselection);
  };

  const resetAfterSave = (
    filterSchoolSubjectId: number | null,
    filterPerformanceAssessmentId: number | null = null,
  ): void => {
    resetFromPreselection({
      schoolSubjectId: filterSchoolSubjectId,
      performanceAssessmentId: filterPerformanceAssessmentId,
    });
  };

  /** the assessment behind an active timeline grouping filter, when it is (or its own subject makes it) eligible */
  const findPreselectableAssessment = (
    performanceAssessmentId: number | null,
  ): Components.Schemas.PerformanceAssessmentDto | null => {
    if (performanceAssessmentId == null) return null;
    const assessment =
      context.allPerformanceAssessments.value.find((candidate) => candidate.id === performanceAssessmentId) ?? null;
    if (!assessment) return null;

    const effectiveSubjectId = draft.schoolSubjectId ?? assessment.schoolSubjectId;
    const effectiveSubject =
      context.allSchoolSubjects.value.find((subject) => subject.id === effectiveSubjectId) ?? null;
    const eligible = getEligiblePerformanceAssessments(context.allPerformanceAssessments.value, effectiveSubject);
    return eligible.some((candidate) => candidate.id === assessment.id) ? assessment : null;
  };

  const applyInitialStudents = (preselectedStudentIds?: readonly number[]): void => {
    const fixedStudentIds = context.fixedContext?.value?.studentIds;
    if (fixedStudentIds !== undefined) {
      draft.allEligibleStudentsSelected = false;
      draft.studentIds = normalizeEligibleStudentIds(uniquePrimitives(fixedStudentIds));
      return;
    }

    const studentIds = normalizeEligibleStudentIds([...(preselectedStudentIds ?? context.defaultStudentIds.value)]);
    draft.allEligibleStudentsSelected = studentIds.length === 0;
    draft.studentIds = studentIds;
  };

  const applyPreselectedCompetences = (preselectedCompetenceIds?: readonly number[]): void => {
    if (context.fixedContext?.value?.competenceIds !== undefined) return;
    for (const competenceId of uniquePrimitives(preselectedCompetenceIds ?? [])) addCompetence(competenceId);
  };

  const applyFixedCompetences = (): void => {
    const fixedCompetenceIds = context.fixedContext?.value?.competenceIds;
    if (fixedCompetenceIds === undefined) return;

    const competenceIds = uniquePrimitives(fixedCompetenceIds).filter(
      (competenceId) =>
        competenceRelationMaps.value.schoolSubjectIdByCompetenceId.get(competenceId) === draft.schoolSubjectId,
    );
    draft.competenceIds = competenceIds;
    draft.competenceLevelById = Object.fromEntries(
      competenceIds.map((competenceId) => [competenceId, draft.competenceLevelById[competenceId] ?? null]),
    );
  };

  const applySubjectChangeImpact = (impact: ISubjectChangeImpact): void => {
    if (impact.droppedCompetenceIds.length) {
      const dropped = new Set(impact.droppedCompetenceIds);
      draft.competenceIds = draft.competenceIds.filter((id) => !dropped.has(id));
      for (const competenceId of impact.droppedCompetenceIds) {
        delete draft.competenceLevelById[competenceId];
      }
    }

    if (impact.droppedStudentIds.length) {
      const dropped = new Set(impact.droppedStudentIds);
      draft.studentIds = draft.studentIds.filter((id) => !dropped.has(id));
      if (!draft.studentIds.length) selectAllEligibleStudents();
    }

    if (impact.droppedPerformanceAssessment) {
      draft.performanceAssessmentId = null;
      clearOverallRating();
      draft.gradeCalculationTagId = null;
    }
  };

  return {
    draft,
    selectedSchoolSubject,
    selectedPerformanceAssessment,
    eligiblePerformanceAssessments,
    eligibleStudents,
    selectedStudents,
    resolvedStudents,
    resolvedStudentIds,
    competenceDomainsForSubject,
    selectedCompetences,
    showOverallRating,
    isPristine,
    nextValidationIssue,
    canSubmit,
    fixedContextValid,
    setText,
    setDate,
    setOverallLevel,
    setCompetenceLevel,
    setGradeCalculationTagId,
    addCompetence,
    removeCompetence,
    clearCompetences,
    addStudent,
    setStudents,
    selectAllEligibleStudents,
    selectSubject,
    selectPerformanceAssessment,
    selectNote,
    selectSupportMeasure,
    initFromPreselection,
    initFromFilter,
    resetFromPreselection,
    resetAfterSave,
  };
};
